import React, { useEffect, useState, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { StarFill, SendFill } from "react-bootstrap-icons";
import { messageError, messageSuccess } from "../Toast/toast";
import {
  getPendingEvaluations,
  dismissEvaluation,
  submitEvaluation,
} from "../../services/nps/nps.service";
import "./NpsEvaluationModal.css";

const SCORES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const MAX_DISMISSALS = 5;

function getScoreCategory(score) {
  if (score <= 6) return "detractor";
  if (score <= 8) return "neutral";
  return "promoter";
}

export default function NpsEvaluationModal() {
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissing, setDismissing] = useState(false);
  const [evaluation, setEvaluation] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [observation, setObservation] = useState("");

  const isMandatory = evaluation?.dismiss_count >= MAX_DISMISSALS;

  const checkPending = useCallback(async () => {
    try {
      const response = await getPendingEvaluations();
      const pending = response.data || [];
      if (pending.length > 0) {
        const next = pending[0];
        if (next.show_on_route && location.pathname !== next.show_on_route) {
          return;
        }
        setEvaluation(next);
        setShow(true);
      }
    } catch {
      // silently fail
    }
  }, [location.pathname]);

  useEffect(() => {
    const timer = setTimeout(checkPending, 3000);
    return () => clearTimeout(timer);
  }, [checkPending]);

  const isFormValid = selectedScore !== null && observation.trim().length >= 5;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setSubmitting(true);
    try {
      await submitEvaluation(evaluation.id, {
        rating: selectedScore,
        observation: observation.trim(),
      });
      messageSuccess("Obrigado pela sua avaliação!");
      setShow(false);
      resetForm();
    } catch (error) {
      messageError(
        error.response?.data?.message || "Erro ao enviar avaliação"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDismiss = async () => {
    setDismissing(true);
    try {
      await dismissEvaluation(evaluation.id);
      setShow(false);
      resetForm();
    } catch {
      messageError("Erro ao dispensar avaliação");
    } finally {
      setDismissing(false);
    }
  };

  const resetForm = () => {
    setSelectedScore(null);
    setObservation("");
    setEvaluation(null);
  };

  return (
    <Modal
      show={show}
      onHide={isMandatory ? undefined : handleDismiss}
      centered
      size="lg"
      className="nps-modal"
      backdrop={isMandatory ? "static" : true}
      keyboard={!isMandatory}
    >
      <Modal.Header className="nps-modal-header border-0">
        <div className="nps-modal-header-content">
          <div className="nps-modal-icon">
            <StarFill size={28} />
          </div>
          <h3 className="nps-modal-title">Sua opinião vale muito!</h3>
          <p className="nps-modal-subtitle">
            Em uma escala de 1 a 10, o quanto você está satisfeito com o{" "}
            <strong>Spartacus?</strong>
          </p>
        </div>
      </Modal.Header>

      <Modal.Body className="nps-modal-body">
        <div className="nps-scale-labels">
          <span className="nps-label-left">Insatisfeito</span>
          <span className="nps-label-right">Satisfeito</span>
        </div>

        <div className="nps-scale-container">
          {SCORES.map((score) => {
            const isActive = selectedScore === score;
            const category = getScoreCategory(score);
            const activeClass = isActive ? `active ${category}` : "";

            return (
              <div
                key={score}
                className={`nps-score-btn ${activeClass}`}
                data-score={score}
                onClick={() => setSelectedScore(score)}
              >
                {score}
              </div>
            );
          })}
        </div>

        <div className="nps-feedback-group">
          <label className="nps-feedback-label">
            Descreva brevemente o motivo da sua avaliação (Obrigatório)
          </label>
          <textarea
            className="nps-feedback-textarea"
            rows={4}
            placeholder="Escreva aqui suas sugestões de melhoria, dificuldades encontradas ou novas funcionalidades que ajudaram sua rotina!"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
          />
        </div>
      </Modal.Body>

      <Modal.Footer className="nps-modal-footer border-0">
        {!isMandatory && (
          <button
            type="button"
            className="nps-dismiss-btn"
            onClick={handleDismiss}
            disabled={dismissing}
          >
            {dismissing ? "Fechando..." : "Responder depois"}
          </button>
        )}
        <button
          type="button"
          className="nps-submit-btn"
          disabled={!isFormValid || submitting}
          onClick={handleSubmit}
        >
          {submitting ? (
            <>
              <Spinner size="sm" className="me-2" /> Enviando...
            </>
          ) : (
            <>
              Enviar Avaliação <SendFill className="ms-2" size={14} />
            </>
          )}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
